import { memo, useEffect, useMemo, useCallback } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  LayoutDashboard,
  PanelLeftClose,
  PanelLeft,
  BookOpen,
  type LucideIcon,
} from 'lucide-react'
import { useLayout } from '@/components/layout/AppLayout'
import { getActiveModules, filterModulesForPermissions } from '@/lib/auth'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore } from '@/stores/uiStore'
import { SidebarGroup } from '@/components/layout/SidebarGroup'
import { useIsMobile } from '@/hooks/useIsMobile'
import { cn } from '@/lib/utils'

const itemVariants = {
  open: { opacity: 1, x: 0 },
  closed: { opacity: 0, x: -8 },
}

interface NavItem {
  path: string
  icon: LucideIcon
  label: string
  isActive?: boolean
  muted?: boolean
  sidebarOpen?: boolean
}

const SidebarItem = memo(function SidebarItem({ path, icon: Icon, label, isActive, muted, sidebarOpen }: NavItem) {
  return (
    <NavLink
      to={path}
      end
      className={({ isActive: active }) =>
        cn(
          'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150',
          !sidebarOpen && 'justify-center px-0',
          muted
            ? 'text-[#637381] opacity-60 hover:bg-[#252f3a]'
            : active || isActive
              ? 'bg-[#00a76f29] text-white border-l-2 border-[#00ab81]'
              : 'text-[#919eab] hover:bg-[#252f3a] hover:text-white',
        )
      }
    >
      <Icon className="h-5 w-5 shrink-0" />
      {sidebarOpen && (
        <motion.span
          variants={itemVariants}
          className="whitespace-nowrap"
        >
          {label}
        </motion.span>
      )}
    </NavLink>
  )
})

export function Sidebar() {
  const { sidebarOpen, toggleSidebar, scopes } = useLayout()
  const location = useLocation()
  const permissions = useAuthStore((s) => s.admin?.permissions) ?? []
  // Billing es un módulo nuevo top-level: sólo se muestra en el menú si el tenant
  // tiene la feature habilitada (sin paywall en el sidebar para este módulo).
  const modules = useMemo(
    () => filterModulesForPermissions(getActiveModules(scopes), permissions).filter((m) => m.scope !== 'billing' || m.subscribed).filter((m) => m.scope !== 'documentation'),
    [scopes, permissions],
  )
  const expandedGroups = useUiStore((s) => s.expandedGroups)
  const toggleGroup = useUiStore((s) => s.toggleGroup)
  const setGroupExpanded = useUiStore((s) => s.setGroupExpanded)
  const setSidebarOpen = useUiStore((s) => s.setSidebarOpen)
  const isMobile = useIsMobile()

  // On mobile the sidebar is an overlay drawer: close it after navigating so it
  // doesn't stay covering the content the user just opened.
  useEffect(() => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // Default to collapsed when switching into the mobile breakpoint so the
  // content isn't squeezed on first load (sidebarOpen persists as true).
  useEffect(() => {
    if (isMobile) setSidebarOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile])

  // Dashboard is always first, not scope-dependent
  const isDashboardActive = location.pathname === '/dashboard' || location.pathname === '/'

  // Auto-expand the group that contains the current route
  useEffect(() => {
    for (const mod of modules) {
      if (mod.children && mod.children.length > 0) {
        const isInGroup =
          mod.children.some(
            (child) => location.pathname === child.path || location.pathname.startsWith(child.path + '/'),
          ) || location.pathname.startsWith(mod.path)
        if (isInGroup) {
          setGroupExpanded(mod.scope, true)
        }
      }
    }
  }, [location.pathname, modules, setGroupExpanded])

  // When clicking a group in collapsed mode, expand the sidebar AND expand the group
  const handleGroupToggle = useCallback(
    (scope: string) => {
      if (!sidebarOpen) {
        setSidebarOpen(true)
        setGroupExpanded(scope, true)
        return
      }
      toggleGroup(scope)
    },
    [sidebarOpen, setSidebarOpen, toggleGroup, setGroupExpanded],
  )

  // Documentation is always first, not scope-dependent
  const isDocumentationActive = location.pathname === '/documentation' || location.pathname.startsWith('/documentation/')

  // Separate modules with children (dropdown groups) from flat modules
  const groupModules = modules.filter((m) => m.children && m.children.length > 0)
  const flatModules = modules.filter((m) => !m.children || m.children.length === 0)

  let itemIndex = 0

  // Mobile: off-canvas overlay drawer (fixed, slides in/out, full 256px width).
  // Desktop: in-flow column that collapses between 256px and 64px.
  const animateProps = isMobile
    ? { width: 256, x: sidebarOpen ? 0 : -256 }
    : { width: sidebarOpen ? 256 : 64, x: 0 }

  return (
    <>
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          aria-hidden="true"
          onClick={toggleSidebar}
        />
      )}
    <motion.aside
      animate={animateProps}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      className={cn(
          'flex flex-col border-r border-[#ffffff14] bg-[#1c252e] overflow-hidden',
        isMobile ? 'fixed inset-y-0 left-0 z-50 shadow-2xl' : 'relative shrink-0',
      )}
    >
      {/* Logo area */}
      <div className="flex h-16 items-center justify-between border-b border-[#ffffff14] px-4">
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2.5"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#00ab81]">
              <span className="text-sm font-bold text-white">Kaff</span>
            </div>
            <span className="font-display text-sm font-bold text-surface-100 tracking-tight">
              Agencia
            </span>
          </motion.div>
        )}
        <button
          onClick={toggleSidebar}
          className="relative z-20 rounded-md p-1.5 text-[#919eab] hover:bg-[#252f3a] hover:text-white transition-colors pointer-events-auto"
          aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {sidebarOpen ? (
            <PanelLeftClose className="h-4 w-4" />
          ) : (
            <PanelLeft className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Click-to-open overlay when sidebar is collapsed */}
      {!isMobile && !sidebarOpen && (
        <div
          className="absolute inset-0 z-10 cursor-pointer"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* Navigation */}
      <nav className={cn('flex-1 space-y-1 overflow-y-auto p-3 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#333d47] [&::-webkit-scrollbar-thumb]:rounded-full', !sidebarOpen && 'pointer-events-none')} style={{ scrollbarWidth: 'thin', scrollbarColor: '#333d47 transparent' }} aria-label="Navegación principal">
        {/* Dashboard — flat item; visible para todos los usuarios. */}
        <motion.div
          key="dashboard"
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0, duration: 0.15 }}
        >
          <SidebarItem
            path="/dashboard"
            icon={LayoutDashboard}
            label="Dashboard"
            isActive={isDashboardActive}
            sidebarOpen={sidebarOpen}
          />
        </motion.div>

        
        {/* Group modules (with children) — rendered as SidebarGroup */}
        {groupModules.map((mod) => {
          const isExpanded = !!expandedGroups[mod.scope]
          itemIndex++

          return (
            <motion.div
              key={mod.scope}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: itemIndex * 0.04, duration: 0.15 }}
            >
              <SidebarGroup
                icon={mod.icon}
                label={mod.label}
                isExpanded={isExpanded}
                onToggle={() => handleGroupToggle(mod.scope)}
                isActive={false}
                sidebarOpen={sidebarOpen}
                subscribed={mod.subscribed}
              >
                {mod.children!.map((child) => (
                  <SidebarItem
                    key={child.path}
                    path={child.path}
                    icon={child.icon || mod.icon}
                    label={child.label}
                    muted={!mod.subscribed}
                    sidebarOpen={sidebarOpen}
                  />
                ))}
              </SidebarGroup>
            </motion.div>
          )
        })}
        {/* Documentación — flat item; visible para todos los usuarios. */}
        <motion.div
          key="documentation"
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.04, duration: 0.15 }}
        >
          <SidebarItem
            path="/documentation"
            icon={BookOpen}
            label="Documentación"
            isActive={isDocumentationActive}
            sidebarOpen={sidebarOpen}
          />
        </motion.div>

        {/* Flat modules (no children) — rendered as regular SidebarItem */}
        {flatModules.map((mod) => {
          const isActive = location.pathname.startsWith(mod.path)
          itemIndex++

          return (
            <motion.div
              key={mod.scope}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: itemIndex * 0.04, duration: 0.15 }}
            >
              <SidebarItem
                path={mod.path}
                icon={mod.icon}
                label={mod.label}
                isActive={isActive}
                sidebarOpen={sidebarOpen}
              />
            </motion.div>
          )
        })}

        {/* Empty state when no modules are contracted */}
        {modules.length === 0 && sidebarOpen && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="px-3 pt-4 text-xs text-surface-500"
          >
            No hay módulos contratados
          </motion.p>
        )}
      </nav>

      {/* Footer (mobile drawer wraps below) */}
      <div className="border-t border-[#ffffff14] p-3">
        <div className={cn(
          'flex items-center gap-2.5 rounded-lg px-3 py-2',
          !sidebarOpen && 'justify-center'
        )}>
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#00ab81]/10 text-[10px] font-bold text-[#00ab81]">
            Kaff
          </div>
          {sidebarOpen && (
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-[#919eab]">Agencia</p>
              <p className="text-[10px] text-surface-500 font-mono">v1.0.0</p>
            </div>
          )}
        </div>
      </div>
    </motion.aside>
    </>
  )
}
